import { useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import styled from 'styled-components'

import {
  BoosterSingleColorIcon,
  ClockFilledIcon,
  FeesFIcon,
  HoldersIcon,
  LiquidityIcon,
  MarketCapIcon,
  SplashPointsSingleColorIcon,
  TransactionsIcon,
  VolumeIcon,
} from '@blade/ui-kit'
import { selectedNetworkAtom } from '@root/entities/network'
import { NetworkIcon } from '@root/entities/network/ui'
import { METRIC_SPAN_LABEL_MAP } from '@root/entities/pair/ui/constants'
import { useTranslation } from '@root/shared/localization'
import {
  BackingIcon,
  BundlerIcon,
  Input,
  InsiderIcon,
  ProfileIcon,
  SearchIcon,
  Separator as Separator_,
  SniperIcon,
} from '@root/shared/ui-kit'

import { ExploreFilterFormSocials } from './explore-filter-form-socials'
import { ExploreFilterInputGroup as InputGroup } from './explore-filter-input-group'
import { ExploreFilterProviders } from './explore-filter-providers'

import type { ExploreFiltersAtom } from '../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: ExploreFiltersAtom
} & ClassNameProps

export const ExploreFilterForm: React.FC<Props> = ({ model: filtersModel }) => {
  const { t } = useTranslation()

  const model = filtersModel.form
  const period = filtersModel.period
  const [network] = useAtom(selectedNetworkAtom)
  const [holdersMin, setHoldersMin] = useAtom(model.minHolders.raw)
  const [holdersMax, setHoldersMax] = useAtom(model.maxHolders.raw)
  const [txMin, setTxMin] = useAtom(model.minTx.raw)
  const [txMax, setTxMax] = useAtom(model.maxTx.raw)
  const [liquidityMin, setLiquidityMin] = useAtom(model.minLiquidityInUsd.raw)
  const [liquidityMax, setLiquidityMax] = useAtom(model.maxLiquidityInUsd.raw)
  const [minDevHolding, setMinDevHolding] = useAtom(model.minDevHolding.raw)
  const [maxDevHolding, setMaxDevHolding] = useAtom(model.maxDevHolding.raw)
  const [minTop10Holding, setMinTop10Holding] = useAtom(
    model.minTop10Holding.raw,
  )
  const [maxTop10Holding, setMaxTop10Holding] = useAtom(
    model.maxTop10Holding.raw,
  )
  const [minInsidersHolding, setMinInsidersHolding] = useAtom(
    model.minInsidersHolding.raw,
  )
  const [maxInsidersHolding, setMaxInsidersHolding] = useAtom(
    model.maxInsidersHolding.raw,
  )
  const [minSnipersHolding, setMinSnipersHolding] = useAtom(
    model.minSnipersHolding.raw,
  )
  const [maxSnipersHolding, setMaxSnipersHolding] = useAtom(
    model.maxSnipersHolding.raw,
  )
  const [minBundlersHolding, setMinBundlersHolding] = useAtom(
    model.minBundlersHolding.raw,
  )
  const [maxBundlersHolding, setMaxBundlersHolding] = useAtom(
    model.maxBundlersHolding.raw,
  )
  const [minAge, setMinAge] = useAtom(model.minAge.raw)
  const [maxAge, setMaxAge] = useAtom(model.maxAge.raw)
  const [volumeMin, setVolumeMin] = useAtom(model.minVolumeInUsd.raw)
  const [volumeMax, setVolumeMax] = useAtom(model.maxVolumeInUsd.raw)
  const [boostersMin, setBoostersMin] = useAtom(model.minBoosters.raw)
  const [splashPointsMin, setSplashPointsMin] = useAtom(
    model.minSplashPoints.raw,
  )

  const [marketCapMin, setMarketCapMin] = useAtom(model.minMarketCap.raw)
  const [marketCapMax, setMarketCapMax] = useAtom(model.maxMarketCap.raw)
  const [keywords, setKeywords] = useAtom(model.keywords.raw)
  const [filtersEditState] = useAtom(model.filtersEditState)
  const [minFee, setMinFee] = useAtom(model.minFee.raw)
  const [maxFee, setMaxFee] = useAtom(model.maxFee.raw)

  return (
    <Root>
      {/* <Group>
        <Checkbox
          label={t('table.filter.mint-auth-disabled')}
          value={checked}
          onChange={setChecked}
        />
        <Checkbox
          label={t('table.audit.cr')}
          value={checked}
          onChange={setChecked}
        />
        <Checkbox
          label={t('table.filter.lp-burned')}
          value={checked}
          onChange={setChecked}
        />
        <Checkbox
          label={t('table.filter.top-10-holders')}
          value={checked}
          onChange={setChecked}
        />
        <Checkbox
          label={t('table.filter.at-least-1-social')}
          value={checked}
          onChange={setChecked}
        />
      </Group>
      <Separator /> */}
      <ExploreFilterProviders model={model} />
      <Separator />
      <ExploreFilterFormSocials model={model} />
      <Separator />

      <>
        <Group>
          <Row>
            <Label>
              <SearchIcon size={18} color="var(--text-11)" />
              <span>{t('common.search')}</span>
            </Label>
            <Input
              size="m"
              placeholder={t('table.filter.keywords-placeholder')}
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
            />
          </Row>
        </Group>
        <Separator />
      </>
      <Group>
        <InputGroup
          label={t('premium.boosters')}
          valueFrom={boostersMin}
          onChangeFrom={setBoostersMin}
          icon={BoosterSingleColorIcon}
        />
        <InputGroup
          label={t('premium.splash-points')}
          valueFrom={splashPointsMin}
          onChangeFrom={setSplashPointsMin}
          icon={SplashPointsSingleColorIcon}
        />
        <InputGroup
          label={t('table.market_cap')}
          endContent="$"
          valueFrom={marketCapMin}
          valueTo={marketCapMax}
          onChangeFrom={setMarketCapMin}
          onChangeTo={setMarketCapMax}
          icon={MarketCapIcon}
        />
        <InputGroup
          label={t('common.liquidity')}
          endContent="$"
          valueFrom={liquidityMin}
          valueTo={liquidityMax}
          onChangeFrom={setLiquidityMin}
          onChangeTo={setLiquidityMax}
          icon={LiquidityIcon}
        />
        <InputGroup
          label={
            period
              ? `${t('common.volume')} (${t(METRIC_SPAN_LABEL_MAP[period])})`
              : t('common.volume')
          }
          endContent="$"
          valueFrom={volumeMin}
          valueTo={volumeMax}
          onChangeFrom={setVolumeMin}
          onChangeTo={setVolumeMax}
          icon={VolumeIcon}
        />
        <InputGroup
          label={
            period
              ? `${t('transactions')} (${t(METRIC_SPAN_LABEL_MAP[period])})`
              : t('transactions')
          }
          valueFrom={txMin}
          valueTo={txMax}
          onChangeFrom={setTxMin}
          onChangeTo={setTxMax}
          icon={TransactionsIcon}
        />
        <InputGroup
          label={t('pair.fees')}
          valueFrom={minFee}
          valueTo={maxFee}
          onChangeFrom={setMinFee}
          onChangeTo={setMaxFee}
          endContent={<NetworkIcon size={12} network={network} />}
          icon={FeesFIcon}
        />
        <InputGroup
          label={t('common.holders')}
          valueFrom={holdersMin}
          valueTo={holdersMax}
          onChangeFrom={setHoldersMin}
          onChangeTo={setHoldersMax}
          icon={HoldersIcon}
        />
        <InputGroup
          label={t('pair.filter.pair-age')}
          valueFrom={minAge}
          valueTo={maxAge}
          onChangeFrom={setMinAge}
          onChangeTo={setMaxAge}
          icon={ClockFilledIcon}
        />
        <InputGroup
          label={t('common.dev_holdings')}
          valueFrom={minDevHolding}
          valueTo={maxDevHolding}
          onChangeFrom={setMinDevHolding}
          onChangeTo={setMaxDevHolding}
          endContent="%"
          icon={BackingIcon}
        />
        <InputGroup
          label={t('trade.holders-charts.top_10_holders')}
          valueFrom={minTop10Holding}
          valueTo={maxTop10Holding}
          onChangeFrom={setMinTop10Holding}
          onChangeTo={setMaxTop10Holding}
          endContent="%"
          icon={ProfileIcon}
        />
        <InputGroup
          label={t('insiders')}
          valueFrom={minInsidersHolding}
          valueTo={maxInsidersHolding}
          onChangeFrom={setMinInsidersHolding}
          onChangeTo={setMaxInsidersHolding}
          endContent="%"
          icon={InsiderIcon}
        />
        <InputGroup
          label={t('trading.audit.snipers')}
          valueFrom={minSnipersHolding}
          valueTo={maxSnipersHolding}
          onChangeFrom={setMinSnipersHolding}
          onChangeTo={setMaxSnipersHolding}
          endContent="%"
          icon={SniperIcon}
        />
        <InputGroup
          label={t('bundlers')}
          valueFrom={minBundlersHolding}
          valueTo={maxBundlersHolding}
          onChangeFrom={setMinBundlersHolding}
          onChangeTo={setMaxBundlersHolding}
          endContent="%"
          icon={BundlerIcon}
        />
      </Group>
      <AnimatePresence>
        {filtersEditState.isRangeExceeded ? (
          <ErrorMessage
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            {t('common.error-range-exceeded')}
          </ErrorMessage>
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Root = styled.div``

const Separator = styled(Separator_)`
  margin: 12px 10px;
`

const Group = styled.fieldset`
  display: grid;
  border: none;
  gap: 10px;
  padding: 0;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;

  align-content: center;
  justify-content: center;
  align-items: center;
`

const Label = styled.label`
  color: var(--text-11);

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 4px;
`

const ErrorMessage = styled(motion.div)`
  color: var(--red-9);

  display: grid;

  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
  text-align: right;

  margin-top: 3px;
  margin-inline-end: 4px;
`
